import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { FiCalendar, FiDatabase, FiEye, FiDownload, FiTrendingUp, FiTrendingDown, FiFileText, FiFile } from 'react-icons/fi';
import api from '../../utils/api';
import { exportToXLSX, exportToPDF, exportToJSON } from '../../utils/exportUtils';
import toast from 'react-hot-toast';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const chartColors = ['#32966c','#e86f4c','#d4a012','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#ef4444','#06b6d4','#84cc16'];

const DatasetDetailPage = () => {
  const { id } = useParams();
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/datasets/${id}`);
        setDataset(res.data.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, [id]);

  const handleExport = (format) => {
    try {
      if (format === 'xlsx') exportToXLSX(dataset);
      else if (format === 'pdf') exportToPDF(dataset);
      else if (format === 'json') exportToJSON(dataset);
      
      setTimeout(() => {
        toast.success(`Berhasil diunduh sebagai ${format.toUpperCase()}`);
      }, 500);
      
      setShowExport(false);
      // Track download
      api.get(`/datasets/export/${id}?format=track`).catch(() => {});
    } catch (e) {
      console.error('Export Error:', e);
      toast.error('Gagal mengunduh file: ' + e.message);
    }
  };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh', paddingTop: 200 }}><div className="spinner" /></div>;
  if (!dataset) return <div className="empty-state" style={{ minHeight: '100vh', paddingTop: 200 }}><h3>Dataset tidak ditemukan</h3></div>;

  // Time series data
  const timeData = dataset.dataPoints?.filter(dp => !dp.kecamatan || dp.kecamatan === 'Total').sort((a, b) => a.tahun - b.tahun) || [];
  const kecData = dataset.dataPoints?.filter(dp => dp.kecamatan && dp.kecamatan !== 'Total').sort((a, b) => b.nilai - a.nilai) || [];

  // Table data from dynamic columns/rows
  const hasTable = dataset.tableColumns?.length > 0 && dataset.tableRows?.length > 0;

  // Dynamic chart from table data
  const renderTableChart = () => {
    if (!hasTable || dataset.chartType === 'none') return null;
    const xKey = dataset.chartXKey || dataset.tableColumns[0]?.key;
    const yKey = dataset.chartYKey || dataset.tableColumns[1]?.key;
    const labelKey = dataset.chartLabelKey || xKey;

    if (!xKey || !yKey) return null;

    const labels = dataset.tableRows.map(r => String(r.data?.[labelKey] || ''));
    const values = dataset.tableRows.map(r => Number(r.data?.[yKey]) || 0);

    const chartType = dataset.chartType || 'bar';

    if (chartType === 'doughnut') {
      return (
        <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Doughnut data={{
            labels,
            datasets: [{ data: values, backgroundColor: chartColors, borderWidth: 3, borderColor: '#fff' }]
          }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { font: { family: 'Plus Jakarta Sans', size: 11 } } } } }} />
        </div>
      );
    }

    const isHorizontal = chartType === 'horizontalBar';
    return (
      <div style={{ height: Math.max(350, isHorizontal ? labels.length * 35 : 350) }}>
        <Bar data={{
          labels,
          datasets: [{ label: dataset.tableColumns.find(c => c.key === yKey)?.label || 'Nilai', data: values, backgroundColor: chartColors.map(c => c + 'cc'), borderRadius: 6, borderSkipped: false }]
        }} options={{
          responsive: true, maintainAspectRatio: false,
          indexAxis: isHorizontal ? 'y' : 'x',
          plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e2733', padding: 12, cornerRadius: 8 } },
          scales: { y: { beginAtZero: true, grid: { color: '#eaf0f5' } }, x: { grid: { display: isHorizontal } } }
        }} />
      </div>
    );
  };

  const lineData = timeData.length > 1 ? {
    labels: timeData.map(d => d.tahun.toString()),
    datasets: [{
      label: dataset.judul,
      data: timeData.map(d => d.nilai),
      borderColor: '#32966c', backgroundColor: 'rgba(50,150,108,0.1)',
      borderWidth: 3, pointBackgroundColor: '#32966c', pointBorderColor: '#fff',
      pointBorderWidth: 2, pointRadius: 6, fill: true, tension: 0.4
    }]
  } : null;

  const barData = kecData.length > 0 ? {
    labels: kecData.map(d => d.kecamatan),
    datasets: [{
      label: dataset.satuan || 'Nilai',
      data: kecData.map(d => d.nilai),
      backgroundColor: kecData.map((_, i) => chartColors[i % chartColors.length] + 'cc'),
      borderRadius: 6, borderSkipped: false
    }]
  } : null;

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: '#1e2733', padding: 12, cornerRadius: 8 } },
    scales: { y: { beginAtZero: false, grid: { color: '#eaf0f5' } }, x: { grid: { display: false } } }
  };

  return (
    <div className="detail-page">
      <div className="container-wide">
        <div className="detail-header">
          <div className="breadcrumb"><Link to="/dataset">← Kembali ke Dataset</Link></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <span className={`card-kategori kat-${dataset.kategori}`} style={{ marginBottom: '0.75rem', display: 'inline-flex' }}>{dataset.kategori}</span>
              <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', marginBottom: '0.75rem' }}>{dataset.judul}</h1>
              <p style={{ color: 'var(--gray-500)', fontSize: '1rem', maxWidth: 700 }}>{dataset.deskripsi}</p>
            </div>
            {/* Export Buttons */}
            <div style={{ position: 'relative' }}>
              <button className="btn btn-primary" onClick={() => setShowExport(!showExport)}>
                <FiDownload size={16} /> Unduh Data
              </button>
              {showExport && (
                <div className="dropdown-menu" style={{ top: 'calc(100% + 4px)', right: 0 }}>
                  <button className="dropdown-item" onClick={() => handleExport('xlsx')}>
                    <FiFile size={15} style={{ color: '#15803d' }} /> <strong>XLSX</strong> <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Excel Spreadsheet</span>
                  </button>
                  <button className="dropdown-item" onClick={() => handleExport('pdf')}>
                    <FiFileText size={15} style={{ color: '#dc2626' }} /> <strong>PDF</strong> <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Dokumen PDF</span>
                  </button>
                  <button className="dropdown-item" onClick={() => handleExport('json')}>
                    <FiDatabase size={15} style={{ color: '#2563eb' }} /> <strong>JSON</strong> <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>Data Mentah</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid" style={{ marginBottom: '2rem' }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--primary-100)', color: 'var(--primary-600)' }}><FiDatabase /></div>
            <div><div className="stat-value">{dataset.nilaiTerakhir?.toLocaleString('id-ID')}</div><div className="stat-label">Nilai Terakhir {dataset.satuan && `(${dataset.satuan})`}</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: dataset.perubahanPersen >= 0 ? '#dcfce7' : '#fee2e2', color: dataset.perubahanPersen >= 0 ? '#15803d' : '#dc2626' }}>
              {dataset.perubahanPersen >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
            </div>
            <div><div className="stat-value" style={{ color: dataset.perubahanPersen >= 0 ? 'var(--success)' : 'var(--danger)' }}>{dataset.perubahanPersen > 0 ? '+' : ''}{dataset.perubahanPersen}%</div><div className="stat-label">Perubahan</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'var(--gold-100)', color: 'var(--gold-600)' }}><FiCalendar /></div>
            <div><div className="stat-value">{dataset.tahunMulai}-{dataset.tahunAkhir}</div><div className="stat-label">Periode Data</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}><FiEye /></div>
            <div><div className="stat-value">{dataset.viewCount || 0}</div><div className="stat-label">Dilihat</div></div>
          </div>
        </div>

        <div className="detail-grid">
          <div>
            {/* Line Chart */}
            {lineData && (
              <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
                <h3>📈 Tren Data {dataset.tahunMulai}-{dataset.tahunAkhir}</h3>
                <div style={{ height: 350 }}><Line data={lineData} options={chartOptions} /></div>
              </div>
            )}

            {/* Kecamatan Bar Chart */}
            {barData && (
              <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
                <h3>📊 Data per Kecamatan</h3>
                <div style={{ height: Math.max(350, kecData.length * 40) }}>
                  <Bar data={barData} options={{ ...chartOptions, indexAxis: 'y', scales: { ...chartOptions.scales, x: { grid: { color: '#eaf0f5' } }, y: { grid: { display: false } } } }} />
                </div>
              </div>
            )}

            {/* Dynamic Table Chart */}
            {hasTable && dataset.chartType !== 'none' && (
              <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
                <h3>📊 Visualisasi Tabel Data</h3>
                {renderTableChart()}
              </div>
            )}

            {/* Dynamic Table View */}
            {hasTable && (
              <div className="table-container" style={{ marginBottom: '1.5rem' }}>
                <div style={{ padding: '1rem 1rem 0.5rem', fontWeight: 600, fontSize: '1rem' }}>📋 Tabel Data</div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>No</th>
                      {dataset.tableColumns.map(col => <th key={col.key}>{col.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {dataset.tableRows.map((row, i) => (
                      <tr key={row._id || i}>
                        <td>{i + 1}</td>
                        {dataset.tableColumns.map(col => (
                          <td key={col.key} style={{ fontWeight: col.type === 'number' || col.type === 'percent' ? 600 : 400 }}>
                            {col.type === 'number' ? Number(row.data?.[col.key] || 0).toLocaleString('id-ID') :
                             col.type === 'percent' ? `${row.data?.[col.key] || 0}%` :
                             row.data?.[col.key] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* DataPoints Table */}
            {dataset.dataPoints?.length > 0 && !hasTable && (
              <div className="table-container" style={{ marginBottom: '1.5rem' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Tahun</th>
                      {kecData.length > 0 && <th>Kecamatan</th>}
                      <th>Nilai {dataset.satuan && `(${dataset.satuan})`}</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataset.dataPoints.map((dp, i) => (
                      <tr key={i}>
                        <td>{dp.tahun}</td>
                        {kecData.length > 0 && <td>{dp.kecamatan || '-'}</td>}
                        <td style={{ fontWeight: 600 }}>{dp.nilai?.toLocaleString('id-ID')}</td>
                        <td style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>{dp.keterangan || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="detail-sidebar">
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div className="card-body">
                <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '0.95rem', marginBottom: '1rem' }}>Informasi Dataset</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
                  <div><span style={{ color: 'var(--gray-400)' }}>Organisasi</span><br/><strong>{dataset.organisasi}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Sumber Data</span><br/><strong>{dataset.sumberData || '-'}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Tipe Data</span><br/><strong style={{ textTransform: 'capitalize' }}>{dataset.tipeData}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Status</span><br/><span className={`badge badge-${dataset.status === 'published' ? 'success' : 'warning'}`}>{dataset.status}</span></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Dibuat oleh</span><br/><strong>{dataset.createdBy?.nama || '-'}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Terakhir diperbarui</span><br/><strong>{new Date(dataset.updatedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>Unduhan</span><br/><strong>{dataset.downloadCount || 0} kali</strong></div>
                </div>
              </div>
            </div>

            {/* Download Card */}
            <div className="card" style={{ marginBottom: '1rem', background: 'linear-gradient(135deg, var(--primary-50), var(--primary-100))', border: '1px solid var(--primary-200)' }}>
              <div className="card-body" style={{ textAlign: 'center' }}>
                <FiDownload size={24} style={{ color: 'var(--primary-600)', marginBottom: '0.5rem' }} />
                <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '0.95rem', marginBottom: '0.75rem' }}>Unduh Dataset</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleExport('xlsx')}>📗 Download XLSX</button>
                  <button className="btn btn-accent btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleExport('pdf')}>📕 Download PDF</button>
                  <button className="btn btn-secondary btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleExport('json')}>📘 Download JSON</button>
                </div>
              </div>
            </div>

            {dataset.tags?.length > 0 && (
              <div className="card">
                <div className="card-body">
                  <h4 style={{ fontFamily: 'var(--font-body)', fontSize: '0.95rem', marginBottom: '0.75rem' }}>Tags</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {dataset.tags.map(tag => <Link key={tag} to={`/dataset?search=${tag}`} className="badge badge-info" style={{ cursor: 'pointer' }}>{tag}</Link>)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatasetDetailPage;
